import { EpiFormsSubmissionResult } from '@_types/epi-forms';
import Button from '@atoms/buttons/Button';
import { camelToSnakeCaseForObj, getClassFunction } from '@helpers/functions';
import getComponentMarkup from '@helpers/getComponentMarkup';
import ErrorSummary from '@molecules/form/error-summary/ErrorSummary';

import { applyStoredUtmFieldsToForm } from '@helpers/utm-attribution.service';
import { applyEmbeddedFormStepValidationErrorSummary } from '@helpers/epiFormValidationUi';

import { setStatus } from './form-submisssion';

const getClass = getClassFunction('kf-m-form');

const elements = document.querySelectorAll(getClass());

const tcEvent = window.tC?.event;

const clearClasses = (element: HTMLElement) => {
  const cl = element.classList;

  for (let i = cl.length; i > 0; i--) {
    cl.remove(cl[0]);
  }
};
const resetForm = (form: HTMLFormElement) => {
  if (form) {
    const formStatusMessage = form.querySelector<HTMLElement>('.Form__Status__Message');
    const formSubmissionMessage = form.querySelector('.Form__SubmissionSuccess__Message');
    const formMainBody = form.querySelector('.Form__MainBody');
    const inputFields = form.querySelectorAll('.ValidationRequired');
    const errorMessages = form.querySelectorAll('.Form__Element__ValidationError');

    form.reset();

    if (formStatusMessage) {
      clearClasses(formStatusMessage);
      formStatusMessage.classList.add('Form__Status__Message', 'hide');
      formSubmissionMessage?.classList.add('Form__SubmissionSuccess__Message', 'hide');
    }

    if (formMainBody) {
      formMainBody.removeAttribute('style');
    }

    if (inputFields) {
      for (let i = 0; i < inputFields.length; ++i) {
        inputFields[i].classList.remove('ValidationSuccess', 'ValidationFailed');
      }
    }
    [].forEach.call(errorMessages, (errorMessage: HTMLElement) => {
      errorMessage.style.display = 'none';
    });
  }
};

const addTryAgainButton = (form: HTMLElement) => {
  const errorWrapper = form.querySelector('.kf-m-form__form-reponse-status');

  if (errorWrapper) {
    const tryAgainBtn = form.querySelector<HTMLElement>('.kf-m-form__try-again');

    if (tryAgainBtn) return;

    const btn = getComponentMarkup(Button, {
      text: 'Try again',
      styleModifier: ['primary'],
      className: 'kf-m-form__try-again',
    });
    const div = document.createElement('div');

    div.classList.add('kf-m-form__try-again-wrapper');
    div.innerHTML = btn;
    const btnEl = div.firstChild as HTMLElement;

    btnEl.addEventListener('click', (e) => {
      e.preventDefault();
      const formEl = form.querySelector('form');

      if (formEl) {
        resetForm(formEl);
      }
      form.dataset.status = 'open';
    });

    errorWrapper.appendChild(div);
  }
};

const addInputEventListener = (form: HTMLElement) => {
  const inputFields = form.querySelectorAll('.FormTextbox__Input');

  [].forEach.call(inputFields, (input: HTMLInputElement) => {
    // Add input event listener to add/remove filled class
    input.addEventListener('input', () => {
      if (input.value === '') {
        input.classList.add('empty');
        input.classList.remove('filled');
      } else {
        input.classList.remove('empty');
        input.classList.add('filled');
      }
    });

    // Add initial class on load, to deal with possible prefilled input.
    if (input.value === '') {
      input.classList.add('empty');
    } else {
      input.classList.add('filled');
    }
  });
};

const handleForm = (form: HTMLElement) => {
  const resetBtn = form.querySelector<HTMLInputElement>('.Form__Element.FormResetButton');

  if (resetBtn) {
    const textValue = resetBtn.value;
    const newBtn = document.createElement('button');

    newBtn.classList.add('kf-a-button');
    newBtn.classList.add('kf-a-button--border-grey');
    newBtn.classList.add('kf-a-button--secondary');
    newBtn.textContent = textValue;
    newBtn.addEventListener('click', () => {
      resetBtn.click();
    });

    resetBtn.classList.add('hide');
    resetBtn.after(newBtn);
  }

  const formSuccessMessage = form.querySelector('.Form__SubmissionSuccess__Message');

  if (formSuccessMessage && form.classList.contains('kf-m-form--embedded')) {
    const div = document.createElement('div');

    div.classList.add('Form__SubmissionSuccess__Button');
    const sendMore = document.createElement('button');

    sendMore.classList.add('kf-a-button');
    sendMore.classList.add('kf-a-button--primary');
    sendMore.textContent = 'Send another message';
    sendMore.addEventListener('click', (e) => {
      e.preventDefault();
      const formEl = form.querySelector('form');

      if (formEl) {
        resetForm(formEl);
      }
      form.dataset.status = 'open';
    });
    div.appendChild(sendMore);
    formSuccessMessage.appendChild(div);
  }

  addTryAgainButton(form);
  addInputEventListener(form);

  form.dataset.status = 'open';
};

[].forEach.call(elements, handleForm);

// epi form
if (typeof window.$$epiforms === 'function') {
  const handleFormSubmitted = (e: EpiFormsSubmissionResult) => {
    const form = e.target as HTMLFormElement;
    const formWrapper = form.closest<HTMLElement>('.kf-m-form--embedded');

    if (!formWrapper) return;
    const dataFormEvent = form.dataset.formEvent;
    const dataAgent = formWrapper.dataset.agent;
    let eventTrackingData = dataFormEvent ? JSON.parse(dataFormEvent) : undefined;

    if (dataAgent) eventTrackingData = { ...eventTrackingData, ...JSON.parse(dataAgent) };

    if (tcEvent && eventTrackingData?.triggerLabel) {
      if (tcEvent && eventTrackingData.triggerLabel && typeof tcEvent[eventTrackingData.triggerLabel] === 'function') {
        tcEvent[eventTrackingData.triggerLabel](e, {
          ...camelToSnakeCaseForObj(eventTrackingData),
          form_status: 'form.submit.success',
          lead_id: e.returnedResult?.additionalParams?.eFormId,
        });
      }
    }

    if (formWrapper) {
      setStatus(form, formWrapper, e);
    }
  };

  const handleErrors = (e: EpiFormsSubmissionResult) => {
    const form = e.target as HTMLFormElement;
    const formWrapper = form.closest<HTMLElement>('.kf-m-form--embedded');

    if (formWrapper) {
      setStatus(form, formWrapper, e);
    }
  };

  const handleFormsStartSubmitting = (e: Event) => {
    const form = e.target as HTMLFormElement;

    applyStoredUtmFieldsToForm(form);

    const formWrapper = form.closest<HTMLElement>('.kf-m-form--embedded');

    if (formWrapper) {
      formWrapper.classList.add('no-scroll');
    }
  };

  window.$$epiforms('.kf-m-form--embedded .EPiServerForms').on('formsSubmitted', handleFormSubmitted);
  window.$$epiforms('.kf-m-form--embedded .EPiServerForms').on('formsSubmittedError', handleErrors);
  window.$$epiforms('.kf-m-form .EPiServerForms').on('formsStartSubmitting', handleFormsStartSubmitting);

  window.$$epiforms('.kf-m-form .EPiServerForms').on('formsStepValidating', (event: EpiFormsSubmissionResult) => {
    const form = event.target as HTMLFormElement;

    applyEmbeddedFormStepValidationErrorSummary(event, form, (errorMessages) => {
      const formMainBody = form.querySelector<HTMLElement>('.Form__MainBody');
      const errorSummaryEl = getComponentMarkup(ErrorSummary, { title: 'There is a problem', items: errorMessages });
      const errorSummary = document.createElement('div');

      errorSummary.innerHTML = errorSummaryEl;
      formMainBody?.before(errorSummary.firstChild as Node);
    });
  });
}
